/**
 * Runtime Configuration Service
 *
 * Loads runtime configuration from /assets/config.json for staging/production.
 * On localhost, Cognito is disabled and the existing environment-based URLs are used.
 */

import { Injectable } from '@angular/core';

export interface CognitoRuntimeConfig {
  userPoolId: string;
  clientId: string;
  region: string;
  domain: string;
  redirectSignIn: string;
  redirectSignOut: string;
  scope: string[];
  responseType: string;
}

export interface RuntimeConfig {
  production: boolean;
  environment: 'dev' | 'stage' | 'prod';
  apiUrl: string;
  authServiceUrl?: string;
  /** Centralized auth-ui URL (e.g. https://auth.stage.tsin.ca) */
  authUiUrl?: string;
  cognito?: CognitoRuntimeConfig;
  generatedAt?: string;
}

@Injectable({
  providedIn: 'root'
})
export class ConfigService {
  private config: RuntimeConfig | null = null;

  getConfig(): RuntimeConfig {
    if (this.config) {
      return this.config;
    }
    return this.getLocalhostConfig();
  }

  setConfig(config: RuntimeConfig): void {
    this.config = config;
    console.log(`✅ Runtime config loaded for ${config.environment.toUpperCase()} environment`);
    if (config.cognito) {
      console.log('   Cognito Redirect:', config.cognito.redirectSignIn);
    } else {
      console.log('   Cognito: disabled (local auth will be used)');
    }
  }

  getEnvironment(): 'dev' | 'stage' | 'prod' {
    return this.config?.environment || 'dev';
  }

  getCognitoConfig(): CognitoRuntimeConfig | undefined {
    return this.getConfig().cognito;
  }

  getAuthServiceUrl(): string | undefined {
    return this.getConfig().authServiceUrl;
  }

  /** Get the centralized auth-ui URL (e.g. https://auth.stage.tsin.ca) */
  getAuthUiUrl(): string | undefined {
    return this.getConfig().authUiUrl;
  }

  private getLocalhostConfig(): RuntimeConfig {
    return {
      production: false,
      environment: 'dev',
      apiUrl: 'http://localhost:3000/api',
    };
  }
}

